import React, { useState, useCallback, useEffect, useRef, useMemo } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Grid,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  InputAdornment,
  Autocomplete,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Chip,
  Paper,
  Avatar,
  // Checkbox, // Commented out - Select list feature
  Menu,
  ListItemIcon,
  IconButton,
  Tooltip,
} from '@mui/material';
import {
  Search as SearchIcon,
  FilterList as FilterIcon,
  PictureAsPdf as PdfIcon,
  Warning as WarningIcon,
  Refresh as RefreshIcon,
  Description as ExcelIcon,
  CheckCircle as CheckCircleIcon,
} from '@mui/icons-material';
import { getBookings, getCommissionGenerator, updateCommissionGenerator, getProjectsPaginated, getPlotsByProjectSearch } from '../../services/project';
import { getAssociates } from '../../services/associate';
import { getLeadInterested } from '../../services/master';
import ExportButton from '../../components/ExportButton/ExportButton';
import RefreshButton from '../../components/RefreshButton/RefreshButton';
import { exportAsPDF, exportAsExcel, exportAsCSV } from '../../utils/importExport';
import { hasWritePermission, hasReadPermission } from '../../utils/menuFilter';
import { useHistory } from 'react-router-dom';
import MoreFilters from '../../components/MoreFilters/MoreFilters';

// Simple debounce utility
const debounce = (func, wait) => {
  let timeout;
  return function executedFunction(...args) {
    const later = () => {
      clearTimeout(timeout);
      func(...args);
    };
    clearTimeout(timeout);
    timeout = setTimeout(later, wait);
  };
};

const CommissionGenerate = () => {
  const history = useHistory();
  const canWriteCommission = hasWritePermission('commission-generation.commission-generation') || hasWritePermission('real-estate-management.commission-generation') || hasWritePermission('commission.commission-generation') || hasWritePermission('commission-generation');
  const canReadCommission = hasReadPermission('commission-generation.commission-generation') || hasReadPermission('real-estate-management.commission-generation') || hasReadPermission('commission.commission-generation') || hasReadPermission('commission-generation');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [totalCount, setTotalCount] = useState(0);
  const [totalPayableCommission, setTotalPayableCommission] = useState(0);
  const [tableSearch, setTableSearch] = useState('');
  // Commented out - Select list feature
  // const [selectedRows, setSelectedRows] = useState([]);
  const [columnMenuAnchor, setColumnMenuAnchor] = useState(null);

  // More Filters States
  const [moreFiltersOpen, setMoreFiltersOpen] = useState(false);
  const [sortBy, setSortBy] = useState('');
  const [tempSortBy, setTempSortBy] = useState('');
  const [dateFilter, setDateFilter] = useState(null);
  const [tempDateFilter, setTempDateFilter] = useState(null);
  const [tempReceiveFrom, setTempReceiveFrom] = useState('');
  const [tempReceiveTo, setTempReceiveTo] = useState('');

  const handleApplyFilters = () => {
    setSortBy(tempSortBy);
    setDateFilter(tempDateFilter);
    setFilters(prev => ({
      ...prev,
      receiveFrom: tempReceiveFrom,
      receiveTo: tempReceiveTo,
    }));
    setPage(0);
    setMoreFiltersOpen(false);
  };

  const handleResetFilters = () => {
    setSortBy('');
    setTempSortBy('');
    setDateFilter(null);
    setTempDateFilter(null);
    setTempReceiveFrom('');
    setTempReceiveTo('');
    setFilters(prev => ({
      ...prev,
      receiveFrom: '',
      receiveTo: '',
    }));
    setPage(0);
  };

  const [filters, setFilters] = useState({
    receiveFrom: '',
    receiveTo: '',
    unitType: '',
    project: '',
    unitNo: '',
    bookingNo: null,
    associateName: null,
    includeTeamMembers: 'Yes',
  });

  // Booking search state
  const [bookingOptions, setBookingOptions] = useState([]);
  const [loadingBookings, setLoadingBookings] = useState(false);
  const [bookingSearchInput, setBookingSearchInput] = useState('');
  const bookingSearchTimeoutRef = useRef(null);

  // Associate dropdown state
  const [associateOptions, setAssociateOptions] = useState([]);
  const [loadingAssociates, setLoadingAssociates] = useState(false);
  const [associateSearchInput, setAssociateSearchInput] = useState('');
  const [associatePage, setAssociatePage] = useState(1);
  const [hasMoreAssociates, setHasMoreAssociates] = useState(true);
  const [loadingMoreAssociates, setLoadingMoreAssociates] = useState(false);
  const associateSearchTimeoutRef = useRef(null);

  // Project dropdown state
  const [projectOptions, setProjectOptions] = useState([]);
  const [loadingProjects, setLoadingProjects] = useState(false);
  const [projectSearchInput, setProjectSearchInput] = useState('');
  const [projectPage, setProjectPage] = useState(1);
  const [hasMoreProjects, setHasMoreProjects] = useState(true);
  const [loadingMoreProjects, setLoadingMoreProjects] = useState(false);
  const projectSearchTimeoutRef = useRef(null);

  // Unit type state
  const [unitTypeOptions, setUnitTypeOptions] = useState([]);
  const [loadingUnitTypes, setLoadingUnitTypes] = useState(false);

  // Unit dropdown state
  const [unitOptions, setUnitOptions] = useState([]);
  const [loadingUnits, setLoadingUnits] = useState(false);
  const [unitSearchInput, setUnitSearchInput] = useState('');
  const [unitPage, setUnitPage] = useState(1);
  const [hasMoreUnits, setHasMoreUnits] = useState(true);
  const [loadingMoreUnits, setLoadingMoreUnits] = useState(false);
  const unitSearchTimeoutRef = useRef(null);

  // Commission data state
  const [commissionData, setCommissionData] = useState([]);
  const [loadingCommission, setLoadingCommission] = useState(false);

  // Confirmation dialog state
  const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);
  const [selectedCommission, setSelectedCommission] = useState(null);
  const [updatingCommission, setUpdatingCommission] = useState(false);

  // Fetch associates with pagination and search
  const fetchAssociates = useCallback(async (search = '', pageNum = 1, isAppend = false) => {
    if (pageNum === 1) {
      setLoadingAssociates(true);
    } else {
      setLoadingMoreAssociates(true);
    }

    try {
      const params = {
        page: pageNum,
        limit: 10, // Request only 10 items as requested
        search: (search || '').trim(),
      };

      const response = await getAssociates(params);

      // Handle different response structures
      let associates = [];
      let totalDocs = 0;

      if (response.status && Array.isArray(response.data)) {
        associates = response.data;
        totalDocs = response.totalCount || associates.length;
      } else if (response.success && Array.isArray(response.data)) {
        associates = response.data;
        totalDocs = response.totalCount || associates.length;
      } else if (Array.isArray(response.data?.docs)) {
        associates = response.data.docs;
        totalDocs = response.data.totalDocs || response.data.total || 0;
      } else if (Array.isArray(response)) {
        associates = response;
        totalDocs = associates.length;
      }

      // Format associates for Autocomplete
      const formattedAssociates = associates.map((associate) => {
        const associateId = associate.associateId || associate._id || associate.id || '';
        const name = associate.name || '';
        const phone = associate.phone || associate.whatsapp || '';
        const code = associate.code || associateId || '';

        return {
          id: associate._id || associate.id,
          name: name,
          code: code,
          phone: phone,
        };
      });

      if (isAppend) {
        setAssociateOptions((prev) => [...prev, ...formattedAssociates]);
      } else {
        setAssociateOptions(formattedAssociates);
      }

      // Update pagination state
      setHasMoreAssociates(formattedAssociates.length === 10);
      setAssociatePage(pageNum);

    } catch (error) {
      console.error('Failed to fetch associates:', error);
    } finally {
      setLoadingAssociates(false);
      setLoadingMoreAssociates(false);
    }
  }, [loadingAssociates, loadingMoreAssociates]);

  // Handle associate search input change
  const handleAssociateInputChange = useCallback((_, value, reason) => {
    setAssociateSearchInput(value);

    if (reason === 'input' || reason === 'clear') {
      // Reset pagination and search
      if (associateSearchTimeoutRef.current) {
        clearTimeout(associateSearchTimeoutRef.current);
      }

      associateSearchTimeoutRef.current = setTimeout(() => {
        setAssociatePage(1);
        setHasMoreAssociates(true);
        fetchAssociates(value, 1, false);
      }, 500);
    }
  }, [fetchAssociates]);

  // Fetch projects with pagination and search
  const fetchProjects = useCallback(async (search = '', pageNum = 1, isAppend = false) => {
    if (pageNum === 1) {
      setLoadingProjects(true);
    } else {
      setLoadingMoreProjects(true);
    }

    try {
      const params = {
        page: pageNum,
        limit: 10,
        search: search,
      };

      const response = await getProjectsPaginated(params);

      let projects = [];
      if (response.status && Array.isArray(response.data)) {
        projects = response.data;
      } else if (response.success && Array.isArray(response.data)) {
        projects = response.data;
      } else if (Array.isArray(response.data?.docs)) {
        projects = response.data.docs;
      } else if (Array.isArray(response)) {
        projects = response;
      }

      // Format projects for Autocomplete
      const formattedProjects = projects.map((project) => ({
        id: project._id || project.id,
        projectName: project.projectName || '',
      }));

      if (isAppend) {
        setProjectOptions((prev) => [...prev, ...formattedProjects]);
      } else {
        setProjectOptions(formattedProjects);
      }

      setHasMoreProjects(formattedProjects.length === 10);
      setProjectPage(pageNum);

    } catch (error) {
      console.error('Failed to fetch projects:', error);
    } finally {
      setLoadingProjects(false);
      setLoadingMoreProjects(false);
    }
  }, []);

  // Handle project search input change
  const handleProjectInputChange = useCallback((_, value, reason) => {
    setProjectSearchInput(value);

    if (reason === 'input' || reason === 'clear') {
      if (projectSearchTimeoutRef.current) {
        clearTimeout(projectSearchTimeoutRef.current);
      }

      projectSearchTimeoutRef.current = setTimeout(() => {
        setProjectPage(1);
        setHasMoreProjects(true);
        fetchProjects(value, 1, false);
      }, 500);
    }
  }, [fetchProjects]);

  // Fetch unit types (project types)
  const fetchUnitTypes = useCallback(async () => {
    try {
      setLoadingUnitTypes(true);
      const data = await getLeadInterested();
      // Filter active types and format them
      const filteredTypes = data
        .filter(item => item.rawStatus)
        .map(item => item.leadInterested);
      setUnitTypeOptions(filteredTypes);
    } catch (error) {
      console.error('Failed to fetch unit types:', error);
    } finally {
      setLoadingUnitTypes(false);
    }
  }, []);

  // Fetch unit numbers based on project and unit type
  const fetchUnits = useCallback(async (search = '', pageNum = 1, isAppend = false) => {
    const projectId = filters.project?.id || filters.project?._id || '';
    const unitType = filters.unitType || '';

    if (!projectId) {
      setUnitOptions([]);
      return;
    }

    if (pageNum === 1) {
      setLoadingUnits(true);
    } else {
      setLoadingMoreUnits(true);
    }

    try {
      const response = await getPlotsByProjectSearch(
        projectId,
        unitType,
        null,
        null,
        search,
        pageNum,
        20
      );

      const inventories = response.inventories || [];

      // Format inventories - use plotName or flatNo or unitNo based on what's available
      const formattedUnits = inventories.map((unit) => ({
        id: unit._id || unit.id,
        unitNo: unit.plotName || unit.unitNo || unit.flatNo || unit.name || 'N/A',
      }));

      if (isAppend) {
        setUnitOptions((prev) => [...prev, ...formattedUnits]);
      } else {
        setUnitOptions(formattedUnits);
      }

      setHasMoreUnits(formattedUnits.length === 20);
      setUnitPage(pageNum);

    } catch (error) {
      console.error('Failed to fetch units:', error);
    } finally {
      setLoadingUnits(false);
      setLoadingMoreUnits(false);
    }
  }, [filters.project, filters.unitType]);

  // Handle unit search input change
  const handleUnitInputChange = useCallback((_, value, reason) => {
    setUnitSearchInput(value);

    if (reason === 'input' || reason === 'clear') {
      if (unitSearchTimeoutRef.current) {
        clearTimeout(unitSearchTimeoutRef.current);
      }

      unitSearchTimeoutRef.current = setTimeout(() => {
        setUnitPage(1);
        setHasMoreUnits(true);
        fetchUnits(value, 1, false);
      }, 500);
    }
  }, [fetchUnits]);

  useEffect(() => {
    fetchUnitTypes();
  }, [fetchUnitTypes]);

  // Refetch units when project or unit type changes
  useEffect(() => {
    setFilters(prev => ({ ...prev, unitNo: '' }));
    setUnitSearchInput('');
    setUnitOptions([]);
    setUnitPage(1);
    setHasMoreUnits(true);
    if (filters.project) {
      fetchUnits('', 1, false);
    }
  }, [filters.project, filters.unitType]);
  const units = ['Plot 5', 'Plot 6', 'A-101', 'A-102', 'RH-01'];
  const includeTeamOptions = ['Yes', 'No'];

  // Sample table data
  const sampleData = [
    // {
    //   id: 1,
    //   bookingDate: '2025-11-22',
    //   receivedDate: '2025-11-25',
    //   unitType: 'PLOT',
    //   project: 'Test Project Vaibhav',
    //   unitDetail: 'Plot 5',
    //   unitSize: '600',
    //   registry: 'Not Done',
    //   associate: 'Vaibhav Test (712152)',
    //   associatePhone: '7668797987',
    //   bookingAmount: 3000000.0000,
    //   costAmount: 3000000.0000,
    //   totalBookingCommission: 6000.00,
    //   receivedAmount: 45000.00,
    //   payableCommissionPercent: 1.500000,
    //   payableCommissionAmount: 90.00,
    //   previousPayableCommissionAmount: 0.00,
    // },
    // {
    //   id: 2,
    //   bookingDate: '2025-11-22',
    //   receivedDate: '2025-11-22',
    //   unitType: 'PLOT',
    //   project: 'Test Project Vaibhav',
    //   unitDetail: 'Plot 5',
    //   unitSize: '600',
    //   registry: 'Not Done',
    //   associate: 'Vaibhav Test (712152)',
    //   associatePhone: '7668797987',
    //   bookingAmount: 3000000.0000,
    //   costAmount: 3000000.0000,
    //   totalBookingCommission: 6000.00,
    //   receivedAmount: 100000.00,
    //   payableCommissionPercent: 3.333333,
    //   payableCommissionAmount: 200.00,
    //   previousPayableCommissionAmount: 0.00,
    // },
  ];

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleFilterChange = (field, value) => {
    setFilters((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  // Fetch bookings with search functionality
  const fetchBookingsList = useCallback(async (searchTerm = '') => {
    if (loadingBookings) return;

    setLoadingBookings(true);
    try {
      const params = { page: 1, limit: 10 };

      // If search term is provided and looks like a number, use bookingId
      // Otherwise, use search parameter
      if (searchTerm && searchTerm.trim()) {
        const trimmedTerm = searchTerm.trim();
        // Check if it's a number (bookingId)
        if (/^\d+$/.test(trimmedTerm)) {
          params.bookingId = trimmedTerm;
        } else {
          params.search = trimmedTerm;
        }
      }

      const response = await getBookings(params);

      // Handle different response structures
      let bookings = [];
      if (response.success || response.status === true) {
        bookings = Array.isArray(response.data) ? response.data : (response.data?.docs || []);
      } else if (Array.isArray(response)) {
        bookings = response;
      } else if (response.data) {
        bookings = Array.isArray(response.data) ? response.data : (response.data?.docs || []);
      }

      // Format bookings for Autocomplete
      const formattedBookings = bookings.map((booking) => {
        const bookingId = booking.bookingId || booking.bookingNo || '';
        const projectName = booking.particular?.projectName || '';
        const inventoryType = booking.particular?.inventoryType || '';
        const inventoryDetail = booking.particular?.inventoryDetail || '';

        // Format: {bookingId} {projectName} {inventoryType} {inventoryDetail}
        const displayLabel = `${bookingId} ${projectName} ${inventoryType} ${inventoryDetail}`.trim();

        return {
          id: booking._id || booking.id, // Store _id for API call
          bookingId: bookingId,
          projectName: projectName,
          inventoryType: inventoryType,
          inventoryDetail: inventoryDetail,
          displayLabel: displayLabel,
          rawData: booking,
        };
      });

      setBookingOptions(formattedBookings);
    } catch (error) {
      console.error('Error fetching bookings:', error);
      setBookingOptions([]);
    } finally {
      setLoadingBookings(false);
    }
  }, [loadingBookings]);

  // Debounced search effect
  const debouncedBookingSearch = useMemo(() => {
    return debounce((searchValue) => {
      const trimmedValue = searchValue.trim();
      if (trimmedValue) {
        fetchBookingsList(trimmedValue);
      } else {
        setBookingOptions([]);
      }
    }, 500);
  }, [fetchBookingsList]);

  // Handle booking search input changes
  useEffect(() => {
    if (bookingSearchTimeoutRef.current) {
      clearTimeout(bookingSearchTimeoutRef.current);
    }

    bookingSearchTimeoutRef.current = setTimeout(() => {
      debouncedBookingSearch(bookingSearchInput);
    }, 300);

    return () => {
      if (bookingSearchTimeoutRef.current) {
        clearTimeout(bookingSearchTimeoutRef.current);
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [bookingSearchInput]);

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (bookingSearchTimeoutRef.current) {
        clearTimeout(bookingSearchTimeoutRef.current);
      }
    };
  }, []);

  const fetchDefaultCommissionData = useCallback(async () => {
    setLoadingCommission(true);
    try {
      const apiParams = {
        page: page + 1,
        limit: rowsPerPage,
        isApproved: false, // Only show unapproved by default
        startDate: filters.receiveFrom,
        endDate: filters.receiveTo,
      };

      if (sortBy) {
        let apiSortBy = '';
        let apiSortOrder = 'asc';

        if (sortBy === 'dateDesc') {
          apiSortBy = 'date';
          apiSortOrder = 'desc';
        } else if (sortBy === 'dateAsc') {
          apiSortBy = 'date';
          apiSortOrder = 'asc';
        } else if (sortBy === 'payableCommissionAmountDesc') {
          apiSortBy = 'payableCommissionAmount';
          apiSortOrder = 'desc';
        } else if (sortBy === 'payableCommissionAmountAsc') {
          apiSortBy = 'payableCommissionAmount';
          apiSortOrder = 'asc';
        } else if (sortBy === 'paymentAmountDesc') {
          apiSortBy = 'paymentAmount';
          apiSortOrder = 'desc';
        } else if (sortBy === 'paymentAmountAsc') {
          apiSortBy = 'paymentAmount';
          apiSortOrder = 'asc';
        }

        if (apiSortBy) {
          apiParams.sortBy = apiSortBy;
          apiParams.sortOrder = apiSortOrder;
        }
      }

      if (dateFilter) {
        apiParams.date = dateFilter;
      }

      const response = await getCommissionGenerator(apiParams);
      if (response.success && Array.isArray(response.data)) {
        const mappedData = response.data.map((item, index) => ({
          id: item._id || `commission-${index}`,
          bookingNo: item.bookingNo || '',
          bookingId: item.bookingId || '',
          bookingDate: item.bookingDate ? new Date(item.bookingDate).toLocaleDateString('en-GB') : '',
          receivedDate: item.receivedDate ? new Date(item.receivedDate).toLocaleDateString('en-GB') : '',
          unitType: item.unitType || '',
          project: item.project || '',
          unitDetail: item.unitDetail || '',
          unitSize: item.unitSize || '',
          registry: item.registry || 'Not Done',
          associate: item.associate || '',
          associatePhone: item.associatePhone || '',
          bookingAmount: typeof item.bookingAmount === 'number' ? item.bookingAmount : 0,
          costAmount: typeof item.costAmount === 'number' ? item.costAmount : 0,
          totalBookingCommission: typeof item.totalBookingCommission === 'number' ? item.totalBookingCommission : 0,
          receivedAmount: typeof item.receivedAmount === 'number' ? item.receivedAmount : 0,
          paymentAmount: typeof item.paymentAmount === 'number' ? item.paymentAmount : 0,
          payableCommissionPercent: typeof item.payableCommissionPercent === 'number' ? item.payableCommissionPercent : 0,
          payableCommissionAmount: typeof item.payableCommissionAmount === 'number' ? item.payableCommissionAmount : 0,
          previousPayableCommissionAmount: typeof item.previousPayableCommissionAmount === 'number' ? item.previousPayableCommissionAmount : 0,
          isApproved: item.isApproved === true || item.isApproved === false ? item.isApproved : false,
          rawReceivedDate: item.receivedDate ? item.receivedDate.split('T')[0] : '', // Store as YYYY-MM-DD for comparison
          _id: item._id || item.createdAt || null,
          createdAt: item.createdAt || null,
        })).filter((item) => item.isApproved === false);
        setCommissionData(mappedData);
        setTotalCount(response.totalItems || response.totalCommissionCount || response.count || mappedData.length);
        setTotalPayableCommission(response.totalPayableCommissionAmount || 0);
      } else {
        setCommissionData([]);
        setTotalCount(0);
        setTotalPayableCommission(0);
      }
    } catch (error) {
      console.error('Failed to fetch default commission data:', error);
      setCommissionData([]);
    } finally {
      setLoadingCommission(false);
    }
  }, [page, rowsPerPage, filters.receiveFrom, filters.receiveTo, sortBy, dateFilter]);

  const handleSearch = useCallback(async () => {
    console.log('Searching with filters:', filters);

    // Validate that at least one filter is selected
    const hasBooking = filters.bookingNo && filters.bookingNo.id;
    const hasAssociate = filters.associateName && filters.associateName.id;
    const hasProject = filters.project && filters.project.id;
    const hasUnitType = !!filters.unitType;
    const hasUnit = filters.unitNo && filters.unitNo.id;

    if (!hasBooking && !hasAssociate && !hasProject && !hasUnitType && !hasUnit) {
      console.error('Please select at least one filter');
      return;
    }

    setLoadingCommission(true);
    try {
      // Build params based on what's selected
      const params = {};

      // bookingId (_id) and bookingNo (human-readable number)
      if (hasBooking) {
        params.bookingId = filters.bookingNo.id; // MongoDB _id
        if (filters.bookingNo.bookingId) {
          params.bookingNo = filters.bookingNo.bookingId; // human-readable number
        }

        // Associate filter
        if (filters.includeTeamMembers === 'Yes') {
          params.associateId = '';
        } else {
          params.associateId = hasAssociate ? filters.associateName.id : '';
        }
      } else if (hasAssociate) {
        params.associateId = filters.associateName.id;
      }

      // projectId
      if (hasProject) {
        params.projectId = filters.project.id;
      }

      // inventoryType / unitType
      if (hasUnitType) {
        params.inventoryType = filters.unitType;
        params.unitType = filters.unitType;
      }

      // inventoryId / unitId
      if (hasUnit) {
        params.inventoryId = filters.unitNo.id;
        params.unitId = filters.unitNo.id;
      }

      // Add pagination and date params
      params.page = page + 1;
      params.limit = rowsPerPage;
      params.startDate = filters.receiveFrom;
      params.endDate = filters.receiveTo;

      if (sortBy) {
        let apiSortBy = '';
        let apiSortOrder = 'asc';

        if (sortBy === 'dateDesc') {
          apiSortBy = 'date';
          apiSortOrder = 'desc';
        } else if (sortBy === 'dateAsc') {
          apiSortBy = 'date';
          apiSortOrder = 'asc';
        } else if (sortBy === 'payableCommissionAmountDesc') {
          apiSortBy = 'payableCommissionAmount';
          apiSortOrder = 'desc';
        } else if (sortBy === 'payableCommissionAmountAsc') {
          apiSortBy = 'payableCommissionAmount';
          apiSortOrder = 'asc';
        } else if (sortBy === 'paymentAmountDesc') {
          apiSortBy = 'paymentAmount';
          apiSortOrder = 'desc';
        } else if (sortBy === 'paymentAmountAsc') {
          apiSortBy = 'paymentAmount';
          apiSortOrder = 'asc';
        }

        if (apiSortBy) {
          params.sortBy = apiSortBy;
          params.sortOrder = apiSortOrder;
        }
      }

      if (dateFilter) {
        params.date = dateFilter;
      }

      const response = await getCommissionGenerator(params);
      console.log('Commission data:', response);

      // Handle the response and update the table data
      if (response.success && Array.isArray(response.data)) {
        // Map API response to table format - matching exact field names from API
        const mappedData = response.data.map((item, index) => ({
          id: item._id || `commission-${index}`, // Use _id from API response
          bookingNo: item.bookingNo || '',
          bookingId: item.bookingId || '',
          bookingDate: item.bookingDate
            ? new Date(item.bookingDate).toLocaleDateString('en-GB')
            : '',
          receivedDate: item.receivedDate
            ? new Date(item.receivedDate).toLocaleDateString('en-GB')
            : '', // receivedDate might not be in response
          unitType: item.unitType || '',
          project: item.project || '',
          unitDetail: item.unitDetail || '',
          unitSize: item.unitSize || '',
          registry: item.registry || 'Not Done', // registry might not be in response
          associate: item.associate || '',
          associatePhone: item.associatePhone || '',
          bookingAmount: typeof item.bookingAmount === 'number' ? item.bookingAmount : 0,
          costAmount: typeof item.costAmount === 'number' ? item.costAmount : 0,
          totalBookingCommission: typeof item.totalBookingCommission === 'number' ? item.totalBookingCommission : 0,
          receivedAmount: typeof item.receivedAmount === 'number' ? item.receivedAmount : 0,
          paymentAmount: typeof item.paymentAmount === 'number' ? item.paymentAmount : 0,
          payableCommissionPercent: typeof item.payableCommissionPercent === 'number' ? item.payableCommissionPercent : 0,
          payableCommissionAmount: typeof item.payableCommissionAmount === 'number' ? item.payableCommissionAmount : 0,
          previousPayableCommissionAmount: typeof item.previousPayableCommissionAmount === 'number' ? item.previousPayableCommissionAmount : 0,
          isApproved: item.isApproved === true || item.isApproved === false ? item.isApproved : false,
          rawReceivedDate: item.receivedDate ? item.receivedDate.split('T')[0] : '', // Store as YYYY-MM-DD for comparison
          _id: item._id || item.createdAt || null, // Store _id or createdAt for API call
          createdAt: item.createdAt || null, // Store createdAt for reference
        }));
        console.log('Mapped commission data:', mappedData);
        console.log('Number of items:', mappedData.length);
        setCommissionData(mappedData);
        setTotalCount(response.totalItems || response.totalCommissionCount || response.count || mappedData.length);
        setTotalPayableCommission(response.totalPayableCommissionAmount || 0);
      } else {
        setCommissionData([]);
        setTotalCount(0);
        setTotalPayableCommission(0);
      }

    } catch (error) {
      console.error('Failed to fetch commission data:', error);
      setCommissionData([]);
      // TODO: Show error message to user
    } finally {
      setLoadingCommission(false);
    }
  }, [page, rowsPerPage, filters, sortBy, dateFilter]);

  useEffect(() => {
    // Only auto-fetch default data if no search is active (runs on pagination changes)
    const hasActiveSearch = (filters.bookingNo && filters.bookingNo.id) ||
      (filters.associateName && filters.associateName.id) ||
      (filters.project && filters.project.id) ||
      !!filters.unitType ||
      (filters.unitNo && filters.unitNo.id);

    if (hasActiveSearch) {
      handleSearch();
    } else {
      fetchDefaultCommissionData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, rowsPerPage, fetchDefaultCommissionData, handleSearch]);

  const handleRefresh = () => {
    const hasActiveSearch = (filters.bookingNo && filters.bookingNo.id) ||
      (filters.associateName && filters.associateName.id) ||
      (filters.project && filters.project.id) ||
      !!filters.unitType ||
      (filters.unitNo && filters.unitNo.id);

    if (hasActiveSearch) {
      handleSearch();
    } else {
      fetchDefaultCommissionData();
    }
  };

  const handleReset = () => {
    setFilters({
      receiveFrom: '',
      receiveTo: '',
      unitType: '',
      project: '',
      unitNo: '',
      bookingNo: null,
      associateName: null,
      includeTeamMembers: 'Yes',
    });
    setBookingSearchInput('');
    setAssociateSearchInput('');
    setProjectSearchInput('');
    setUnitSearchInput('');
    setTableSearch('');
    setAssociateSearchInput('');
    setAssociatePage(1);
    setHasMoreAssociates(true);
    setSortBy('');
    setTempSortBy('');
    setDateFilter(null);
    setTempDateFilter(null);
    setTempReceiveFrom('');
    setTempReceiveTo('');
    setPage(0);
  };

  // Handlers for Booking Autocomplete
  const handleBookingInputChange = useCallback((_, value, reason) => {
    // Only update input when user is typing or clearing
    // Don't update on 'reset' (which happens when option is selected) to avoid interfering with selection
    if (reason === 'input' || reason === 'clear') {
      setBookingSearchInput(value);
    }
  }, []);

  const handleBookingChange = useCallback((event, value) => {
    setFilters((prev) => {
      const newFilters = { ...prev, bookingNo: value };

      // Auto-populate associate from booking data
      if (value && value.rawData && value.rawData.associate) {
        const associate = value.rawData.associate;
        // Format associate to match Autocomplete structure
        const formattedAssociate = {
          id: associate.associateId || associate._id || associate.id,
          name: associate.name || '',
          code: associate.code || associate.phone || '',
          phone: associate.phone || '',
        };
        newFilters.associateName = formattedAssociate;
      } else if (!value) {
        // Clear associate when booking is cleared
        newFilters.associateName = null;
      }

      return newFilters;
    });

    if (value && value.rawData && value.rawData.associate) {
      const associate = value.rawData.associate;
      setAssociateSearchInput(`${associate.name || ''} - ${associate.code || associate.phone || ''}`);
    } else if (!value) {
      setAssociateSearchInput('');
    }

    if (value && value.displayLabel) {
      setBookingSearchInput(value.displayLabel);
    } else {
      setBookingSearchInput('');
      setBookingOptions([]);
    }
  }, []);



  // Commented out - Generate Payable Commission feature
  // const handleGenerateCommission = () => {
  //   console.log('Generating payable commission');
  //   // Handle generate commission logic here
  // };

  // Commented out - Select list feature handlers
  // const handleSelectAll = (event) => {
  //   if (event.target.checked) {
  //     setSelectedRows(filteredData.map((row) => row.id));
  //   } else {
  //     setSelectedRows([]);
  //   }
  // };

  // const handleSelectRow = (id) => {
  //   setSelectedRows((prev) =>
  //     prev.includes(id) ? prev.filter((rowId) => rowId !== id) : [...prev, id]
  //   );
  // };

  const buildExportRows = () =>
    filteredData.map(r => ({
      'Booking Date': r.bookingDate,
      'Received Date': r.receivedDate,
      'Unit Type': r.unitType,
      'Project': r.project,
      'Unit Detail': r.unitDetail,
      'Unit Size': r.unitSize,
      'Registry': r.registry,
      'Associate': r.associate,
      'Phone': r.associatePhone,
      'Payment Amount': r.paymentAmount ? `₹${r.paymentAmount.toLocaleString('en-IN')}` : '₹0',
      'Comm (%)': r.payableCommissionPercent.toFixed(2),
      'Comm Amt': r.payableCommissionAmount.toFixed(2),
      'Prev Comm Amt': r.previousPayableCommissionAmount.toFixed(2),
    }));

  const handleExportExcel = () => {
    const rows = buildExportRows();
    if (!rows.length) return;
    exportAsExcel({ rows, sheetName: 'Commission Report', fileNameBase: 'commission_report' });
  };

  const handleExportCsv = () => {
    const rows = buildExportRows();
    if (!rows.length) return;
    exportAsCSV({ rows, fileNameBase: 'commission_report' });
  };

  const handleExportPdf = () => {
    const rows = buildExportRows();
    if (!rows.length) return;
    exportAsPDF({
      rows,
      title: 'Commission Generation Report',
      fileNameBase: 'commission_report',
      orientation: 'l',
    });
  };


  // Handle make payable button click
  const handleMakePayableClick = (commission) => {
    setSelectedCommission(commission);
    setConfirmDialogOpen(true);
  };

  // Handle confirm make payable
  const handleConfirmMakePayable = async () => {
    if (!selectedCommission) {
      console.error('No commission selected');
      return;
    }

    // Use _id or createdAt as identifier for API call
    const commissionId = selectedCommission._id || selectedCommission.createdAt || selectedCommission.id;
    if (!commissionId) {
      console.error('No commission ID available');
      return;
    }

    setUpdatingCommission(true);
    try {
      const response = await updateCommissionGenerator(commissionId, {
        isApproved: true,
      });

      console.log('Commission updated:', response);

      // Update the commission in the list
      setCommissionData((prev) =>
        prev.map((item) =>
          item._id === selectedCommission._id
            ? { ...item, isApproved: true }
            : item
        )
      );

      setConfirmDialogOpen(false);
      setSelectedCommission(null);

      // TODO: Show success message
    } catch (error) {
      console.error('Failed to update commission:', error);
      // TODO: Show error message to user
    } finally {
      setUpdatingCommission(false);
    }
  };

  // Handle cancel make payable
  const handleCancelMakePayable = () => {
    setConfirmDialogOpen(false);
    setSelectedCommission(null);
  };

  // Filter data based on filters and table search
  // Use commission data if available, otherwise use sample data
  let filteredData = commissionData.length > 0 ? commissionData : sampleData;

  // Only apply filters if they have values
  // For commission data, be lenient with date filters since receivedDate might not exist
  if (filters.receiveFrom && filters.receiveFrom.trim()) {
    filteredData = filteredData.filter((item) => {
      // If rawReceivedDate doesn't exist or is empty, include the item
      if (!item.rawReceivedDate || item.rawReceivedDate === '') return true;
      return item.rawReceivedDate >= filters.receiveFrom;
    });
  }
  if (filters.receiveTo && filters.receiveTo.trim()) {
    filteredData = filteredData.filter((item) => {
      // If rawReceivedDate doesn't exist or is empty, include the item
      if (!item.rawReceivedDate || item.rawReceivedDate === '') return true;
      return item.rawReceivedDate <= filters.receiveTo;
    });
  }
  if (commissionData.length === 0 && filters.project) {
    const projectSearch = typeof filters.project === 'string' ? filters.project : filters.project.projectName;
    if (projectSearch && projectSearch.trim()) {
      filteredData = filteredData.filter((item) => item.project === projectSearch);
    }
  }

  if (commissionData.length === 0 && filters.unitNo) {
    const unitSearch = typeof filters.unitNo === 'string' ? filters.unitNo : filters.unitNo.unitNo;
    if (unitSearch && unitSearch.trim()) {
      filteredData = filteredData.filter((item) => item.unitDetail && item.unitDetail.includes(unitSearch));
    }
  }
  // Don't apply associate filter to commission data - it should show all associates for the booking
  // Only apply associate filter to sample data
  if (commissionData.length === 0 && filters.associateName && filters.associateName.name) {
    filteredData = filteredData.filter((item) =>
      item.associate && item.associate.includes(filters.associateName.name)
    );
  }
  if (tableSearch) {
    const searchLower = tableSearch.toLowerCase();
    filteredData = filteredData.filter(
      (item) =>
        item.bookingDate.includes(searchLower) ||
        item.project.toLowerCase().includes(searchLower) ||
        item.associate.toLowerCase().includes(searchLower) ||
        item.unitDetail.toLowerCase().includes(searchLower)
    );
  }

  console.log('After filtering - filteredData length:', filteredData.length, 'Items:', filteredData);

  // Calculate totals
  const totals = filteredData.reduce(
    (acc, row) => ({
      bookingAmount: acc.bookingAmount + row.bookingAmount,
      costAmount: acc.costAmount + row.costAmount,
      totalBookingCommission: acc.totalBookingCommission + row.totalBookingCommission,
      receivedAmount: acc.receivedAmount + row.receivedAmount,
      paymentAmount: acc.paymentAmount + (row.paymentAmount || 0),
      payableCommissionPercent: acc.payableCommissionPercent + row.payableCommissionPercent,
      payableCommissionAmount: acc.payableCommissionAmount + row.payableCommissionAmount,
      previousPayableCommissionAmount:
        acc.previousPayableCommissionAmount + row.previousPayableCommissionAmount,
    }),
    {
      bookingAmount: 0,
      costAmount: 0,
      totalBookingCommission: 0,
      receivedAmount: 0,
      paymentAmount: 0,
      payableCommissionPercent: 0,
      payableCommissionAmount: 0,
      previousPayableCommissionAmount: 0,
    }
  );

  if (!canReadCommission) {
    return (
      <Box sx={{ p: 4, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', minHeight: '70vh' }}>
        <Paper elevation={3} sx={{ p: 5, textAlign: 'center', borderRadius: 4, maxWidth: 500 }}>
          <Avatar sx={{ bgcolor: '#fee2e2', color: '#ef4444', width: 80, height: 80, mb: 3, mx: 'auto' }}>
            <WarningIcon sx={{ fontSize: 50 }} />
          </Avatar>
          <Typography variant="h4" fontWeight="bold" color="#1a202c" gutterBottom>
            Access Denied
          </Typography>
          <Typography variant="body1" color="text.secondary" sx={{ mb: 4, fontSize: '1.1rem' }}>
            You do not have permission to view Commission Generation. Please contact your administrator.
          </Typography>
          <Button
            variant="contained"
            onClick={() => history.push('/app/dashboard')}
            sx={{
              backgroundColor: '#536DFE',
              '&:hover': { backgroundColor: '#3d5afe' },
              borderRadius: 2,
              px: 4,
              py: 1.5,
              textTransform: 'none',
              fontWeight: 'bold'
            }}
          >
            Back to Dashboard
          </Button>
        </Paper>
      </Box>
    );
  }

  return (
    <Box sx={{ p: { xs: 2, md: 3 }, backgroundColor: '#f5f5f5', minHeight: '100vh' }}>
      {/* Header */}
      <Box sx={{ mb: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 2 }}>
        <Box>
          <Typography variant="h4" sx={{ fontWeight: 700, color: '#000', mb: 0.5, fontSize: { xs: '1rem', md: '2rem' } }}>
            Commission Generation
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'center', flexWrap: 'wrap' }}>
          {canWriteCommission && (
            <ExportButton
              onExportPdf={handleExportPdf}
              onExportCsv={handleExportCsv}
              onExportExcel={handleExportExcel}
              variant="outlined"
              sx={{
                px: 2.5,
                py: 1.2,
                fontSize: '15px'
              }}
            />
          )}
          <RefreshButton
            onClick={handleRefresh}
            loading={loadingCommission}
            sx={{
              fontWeight: 600,
              px: 2,
              fontSize: '15px',
              borderRadius: 2,
              height: { xs: 'auto', sm: 44 }
            }}
          />
        </Box>
      </Box>

      {/* Filter Criteria Section */}
      <Card sx={{ boxShadow: '0 2px 8px rgba(0,0,0,0.08)', borderRadius: 2, mb: 3 }}>
        <CardContent>
          <Typography variant="h6" sx={{ fontWeight: 700, mb: 2 }}>
            Filter Criteria
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6} md={3}>
              <FormControl fullWidth size="small">
                <InputLabel>Select Unit Type</InputLabel>
                <Select
                  value={filters.unitType}
                  label="Select Unit Type"
                  onChange={(e) => handleFilterChange('unitType', e.target.value)}
                  disabled={loadingUnitTypes}
                >
                  <MenuItem value="">
                    <em>Select Unit Type</em>
                  </MenuItem>
                  {unitTypeOptions.map((type) => (
                    <MenuItem key={type} value={type}>
                      {type}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Autocomplete
                fullWidth
                size="small"
                options={projectOptions}
                getOptionLabel={(option) => {
                  if (typeof option === 'string') return option;
                  return option.projectName || '';
                }}
                isOptionEqualToValue={(option, value) => {
                  if (!option || !value) return false;
                  const optionId = String(option.id || option._id || '');
                  const valueId = String(value.id || value._id || '');
                  return optionId === valueId || option.projectName === value.projectName;
                }}
                value={filters.project}
                onChange={(event, newValue) => handleFilterChange('project', newValue)}
                onInputChange={handleProjectInputChange}
                inputValue={projectSearchInput}
                onOpen={() => {
                  if (projectOptions.length === 0 && !loadingProjects) {
                    fetchProjects('', 1, false);
                  }
                }}
                loading={loadingProjects}
                ListboxProps={{
                  style: { maxHeight: '300px' },
                  onScroll: (event) => {
                    const listboxNode = event.currentTarget;
                    const scrollPercentage = (listboxNode.scrollTop + listboxNode.clientHeight) / listboxNode.scrollHeight;
                    if (scrollPercentage >= 0.8 && hasMoreProjects && !loadingMoreProjects && !loadingProjects) {
                      fetchProjects(projectSearchInput, projectPage + 1, true);
                    }
                  },
                }}
                renderOption={(props, option, state) => {
                  const isLast = state.index === projectOptions.length - 1;
                  return (
                    <React.Fragment key={option.id}>
                      <Box component="li" {...props}>
                        <Typography variant="body2">
                          {option.projectName}
                        </Typography>
                      </Box>
                      {isLast && loadingMoreProjects && (
                        <Box sx={{ display: 'flex', justifyContent: 'center', p: 1 }}>
                          <CircularProgress size={20} />
                        </Box>
                      )}
                    </React.Fragment>
                  );
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Select Project"
                    placeholder="Search Project"
                    InputProps={{
                      ...params.InputProps,
                      endAdornment: (
                        <>
                          {loadingProjects ? <CircularProgress color="inherit" size={18} /> : null}
                          {params.InputProps.endAdornment}
                        </>
                      ),
                    }}
                  />
                )}
                noOptionsText={loadingProjects ? 'Loading projects...' : 'No projects found'}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Autocomplete
                fullWidth
                size="small"
                options={unitOptions}
                getOptionLabel={(option) => {
                  if (typeof option === 'string') return option;
                  return option.unitNo || '';
                }}
                isOptionEqualToValue={(option, value) => {
                  if (!option || !value) return false;
                  const optionId = String(option.id || option._id || '');
                  const valueId = String(value.id || value._id || '');
                  return optionId === valueId || option.unitNo === value.unitNo;
                }}
                value={filters.unitNo}
                onChange={(event, newValue) => handleFilterChange('unitNo', newValue)}
                onInputChange={handleUnitInputChange}
                inputValue={unitSearchInput}
                onOpen={() => {
                  if (unitOptions.length === 0 && !loadingUnits && filters.project) {
                    fetchUnits('', 1, false);
                  }
                }}
                loading={loadingUnits}
                disabled={!filters.project}
                ListboxProps={{
                  style: { maxHeight: '300px' },
                  onScroll: (event) => {
                    const listboxNode = event.currentTarget;
                    const scrollPercentage = (listboxNode.scrollTop + listboxNode.clientHeight) / listboxNode.scrollHeight;
                    if (scrollPercentage >= 0.8 && hasMoreUnits && !loadingMoreUnits && !loadingUnits) {
                      fetchUnits(unitSearchInput, unitPage + 1, true);
                    }
                  },
                }}
                renderOption={(props, option, state) => {
                  const isLast = state.index === unitOptions.length - 1;
                  return (
                    <React.Fragment key={option.id}>
                      <Box component="li" {...props}>
                        <Typography variant="body2">
                          {option.unitNo}
                        </Typography>
                      </Box>
                      {isLast && loadingMoreUnits && (
                        <Box sx={{ display: 'flex', justifyContent: 'center', p: 1 }}>
                          <CircularProgress size={20} />
                        </Box>
                      )}
                    </React.Fragment>
                  );
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Select Unit"
                    placeholder="Search Unit"
                    InputProps={{
                      ...params.InputProps,
                      endAdornment: (
                        <>
                          {loadingUnits ? <CircularProgress color="inherit" size={18} /> : null}
                          {params.InputProps.endAdornment}
                        </>
                      ),
                    }}
                  />
                )}
                noOptionsText={!filters.project ? 'Please select project first' : (loadingUnits ? 'Loading units...' : 'No units found')}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Autocomplete
                fullWidth
                size="small"
                options={bookingOptions}
                getOptionLabel={(option) => {
                  if (typeof option === 'string') return option;
                  return option.displayLabel || `Booking ${option.bookingId || ''}`;
                }}
                isOptionEqualToValue={(option, value) => option.id === value?.id}
                loading={loadingBookings}
                inputValue={bookingSearchInput}
                onInputChange={handleBookingInputChange}
                value={filters.bookingNo}
                onChange={handleBookingChange}
                clearOnBlur={false}
                selectOnFocus
                handleHomeEndKeys
                freeSolo
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Booking No."
                    placeholder="Search Booking No. (e.g., 1, 2, 3...)"
                    InputProps={{
                      ...params.InputProps,
                      startAdornment: (
                        <InputAdornment position="start">
                          <SearchIcon sx={{ fontSize: 18 }} />
                        </InputAdornment>
                      ),
                      endAdornment: (
                        <>
                          {loadingBookings ? <CircularProgress color="inherit" size={18} /> : null}
                          {params.InputProps.endAdornment}
                        </>
                      ),
                    }}
                  />
                )}
                noOptionsText={
                  loadingBookings ? 'Loading bookings...' : 'No bookings found'
                }
                renderOption={(props, option) => (
                  <Box component="li" {...props}>
                    <Typography variant="body2">
                      {option.displayLabel}
                    </Typography>
                  </Box>
                )}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Autocomplete
                options={associateOptions}
                getOptionLabel={(option) => {
                  if (!option) return '';
                  return `${option.name || ''} - ${option.code || option.phone || ''}`;
                }}
                isOptionEqualToValue={(option, value) => {
                  if (!option || !value) return false;
                  const optionId = String(option.id || option._id || '');
                  const valueId = String(value.id || value._id || '');
                  return optionId === valueId || (option.name === value.name && option.code === value.code);
                }}
                value={filters.associateName}
                onChange={(event, newValue) => handleFilterChange('associateName', newValue)}
                onInputChange={handleAssociateInputChange}
                inputValue={associateSearchInput}
                onOpen={() => {
                  if (associateOptions.length === 0 && !loadingAssociates) {
                    fetchAssociates('', 1, false);
                  }
                }}
                loading={loadingAssociates}
                disabled={!!filters.bookingNo && !!filters.bookingNo.id}
                ListboxProps={{
                  style: { maxHeight: '300px' },
                  onScroll: (event) => {
                    const listboxNode = event.currentTarget;
                    const scrollPercentage = (listboxNode.scrollTop + listboxNode.clientHeight) / listboxNode.scrollHeight;
                    if (scrollPercentage >= 0.8 && hasMoreAssociates && !loadingMoreAssociates && !loadingAssociates) {
                      fetchAssociates(associateSearchInput, associatePage + 1, true);
                    }
                  },
                }}
                renderOption={(props, option, state) => {
                  const isLast = state.index === associateOptions.length - 1;
                  return (
                    <React.Fragment key={option.id}>
                      <Box component="li" {...props}>
                        <Typography variant="body2">
                          {option.name} - {option.code}
                        </Typography>
                      </Box>
                      {isLast && loadingMoreAssociates && (
                        <Box sx={{ display: 'flex', justifyContent: 'center', p: 1 }}>
                          <CircularProgress size={20} />
                        </Box>
                      )}
                    </React.Fragment>
                  );
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Associate Name"
                    size="small"
                    placeholder={filters.bookingNo && filters.bookingNo.id ? "Auto-filled from booking" : "Select Associate"}
                    required
                    helperText={filters.bookingNo && filters.bookingNo.id ? "Associate is auto-filled from selected booking" : ""}
                    InputProps={{
                      ...params.InputProps,
                      endAdornment: (
                        <>
                          {loadingAssociates ? <CircularProgress color="inherit" size={18} /> : null}
                          {params.InputProps.endAdornment}
                        </>
                      ),
                    }}
                  />
                )}
                noOptionsText={loadingAssociates ? 'Loading associates...' : 'No associates found'}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <FormControl fullWidth size="small">
                <InputLabel>Include Team Members</InputLabel>
                <Select
                  value={filters.includeTeamMembers}
                  label="Include Team Members"
                  onChange={(e) => handleFilterChange('includeTeamMembers', e.target.value)}
                >
                  {includeTeamOptions.map((option) => (
                    <MenuItem key={option} value={option}>
                      {option}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Box sx={{ display: 'flex', gap: 1 }}>

                <Button
                  variant="contained"
                  color="primary"
                  startIcon={<SearchIcon />}
                  onClick={handleSearch}
                  fullWidth
                  sx={{
                    textTransform: 'none',
                    fontWeight: 600,
                    py: 1.5,
                    borderRadius: 2,
                    height: '40px'
                  }}
                >
                  Search
                </Button>
                <Button
                  variant="outlined"
                  startIcon={<RefreshIcon />}
                  onClick={handleReset}
                  fullWidth
                  sx={{
                    textTransform: 'none',
                    fontWeight: 600,
                    py: 1.5,
                    borderRadius: 2,
                    height: '40px'
                  }}
                >
                  Reset
                </Button>
              </Box>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <MoreFilters
                expanded={moreFiltersOpen}
                onToggle={() => setMoreFiltersOpen(!moreFiltersOpen)}
                sortBy={tempSortBy}
                onChangeSortBy={setTempSortBy}
                onApply={handleApplyFilters}
                onReset={handleResetFilters}
                type="commissionGeneration"
                height="40px"
              >
                <Grid container spacing={1.5}>
                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      size="small"
                      label="Search by Single Day"
                      type="date"
                      InputLabelProps={{ shrink: true }}
                      value={tempDateFilter || ''}
                      onChange={(e) => {
                        const val = e.target.value || null;
                        setTempDateFilter(val);
                        if (val) {
                          setTempReceiveFrom('');
                          setTempReceiveTo('');
                        }
                      }}
                    />
                  </Grid>
                  <Grid item xs={6}>
                    <TextField
                      fullWidth
                      size="small"
                      label="Receive From"
                      type="date"
                      InputLabelProps={{ shrink: true }}
                      value={tempReceiveFrom || ''}
                      onChange={(e) => {
                        const val = e.target.value;
                        setTempReceiveFrom(val);
                        if (val) {
                          setTempDateFilter(null);
                        }
                      }}
                    />
                  </Grid>
                  <Grid item xs={6}>
                    <TextField
                      fullWidth
                      size="small"
                      label="Receive To"
                      type="date"
                      InputLabelProps={{ shrink: true }}
                      value={tempReceiveTo || ''}
                      onChange={(e) => {
                        const val = e.target.value;
                        setTempReceiveTo(val);
                        if (val) {
                          setTempDateFilter(null);
                        }
                      }}
                    />
                  </Grid>
                </Grid>
              </MoreFilters>
            </Grid>
          </Grid>
          {/* <Box sx={{ mt: 2 }}>
            <Button
              variant="contained"
              color="primary"
              startIcon={<GenerateIcon />}
              onClick={handleGenerateCommission}
              sx={{
                textTransform: 'none',
                fontWeight: 600,
                px: 3,
                py: 1.5,
                borderRadius: 2,
              }}
            >
              Generate Payable Commission
            </Button>
          </Box> */}
        </CardContent>
      </Card>

      {/* List Section */}
      <Card sx={{ boxShadow: '0 2px 8px rgba(0,0,0,0.08)', borderRadius: 2 }}>
        <CardContent>
          <Box sx={{ mb: 2 }}>
            {/* <Typography variant="h6" sx={{ fontWeight: 700 }}>
              List
            </Typography> */}
          </Box>

          {/* <Box sx={{ display: 'flex', flexDirection: { xs: 'column-reverse', sm: 'row' }, justifyContent: 'space-between', alignItems: 'center', mb: 2, gap: 2 }}> */}
          {/* <FormControl size="small" sx={{ minWidth: 120, width: { xs: '100%', sm: 'auto' } }}>
              <Select
                value={rowsPerPage}
                onChange={(e) => {
                  setRowsPerPage(parseInt(e.target.value, 10));
                  setPage(0);
                }}
                displayEmpty
              >
                <MenuItem value={5}>Show 5 entries</MenuItem>
                <MenuItem value={10}>Show 10 entries</MenuItem>
                <MenuItem value={25}>Show 25 entries</MenuItem>
                <MenuItem value={50}>Show 50 entries</MenuItem>
              </Select>
            </FormControl> */}
          {/* <TextField
              size="small"
              placeholder="Search:"
              value={tableSearch}
              onChange={(e) => setTableSearch(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon fontSize="small" />
                  </InputAdornment>
                ),
              }}
              sx={{ width: { xs: '100%', sm: 250 } }}
            /> */}
          {/* </Box> */}

          <TableContainer sx={{ overflowX: 'auto' }}>
            <Table>
              <TableHead>
                <TableRow sx={{ backgroundColor: '#536DFE' }} >
                  {/* Commented out - Select list checkbox column */}
                  {/* <TableCell padding="checkbox" sx={{ color: '#fff' }}>
                    <Checkbox
                      indeterminate={selectedRows.length > 0 && selectedRows.length < filteredData.length}
                      checked={filteredData.length > 0 && selectedRows.length === filteredData.length}
                      onChange={handleSelectAll}
                      sx={{ color: '#fff', '&.Mui-checked': { color: '#fff' } }}
                    />
                  </TableCell> */}
                  <TableCell sx={{ color: '#fff', fontWeight: 600, fontSize: '12px' }}>Booking Date</TableCell>
                  <TableCell sx={{ color: '#fff', fontWeight: 600, fontSize: '12px' }}>Booking No</TableCell>
                  <TableCell sx={{ color: '#fff', fontWeight: 600, fontSize: '12px' }}>Received Date</TableCell>
                  <TableCell sx={{ color: '#fff', fontWeight: 600, fontSize: '12px' }}>Unit Type</TableCell>
                  <TableCell sx={{ color: '#fff', fontWeight: 600, fontSize: '12px' }}>Project</TableCell>
                  <TableCell sx={{ color: '#fff', fontWeight: 600, fontSize: '12px' }}>Unit Detail</TableCell>
                  <TableCell sx={{ color: '#fff', fontWeight: 600, fontSize: '12px' }}>Unit Size</TableCell>
                  <TableCell sx={{ color: '#fff', fontWeight: 600, fontSize: '12px' }}>Registry</TableCell>
                  <TableCell sx={{ color: '#fff', fontWeight: 600, fontSize: '12px' }}>Associate</TableCell>
                  <TableCell sx={{ color: '#fff', fontWeight: 600, fontSize: '12px' }}>Associate Phone</TableCell>
                  <TableCell sx={{ color: '#fff', fontWeight: 600, fontSize: '12px', textAlign: 'right' }}>Payment Amount</TableCell>
                  <TableCell sx={{ color: '#fff', fontWeight: 600, fontSize: '12px', textAlign: 'right' }}>Payable Commission (%)</TableCell>
                  <TableCell sx={{ color: '#fff', fontWeight: 600, fontSize: '12px', textAlign: 'right' }}>Payable Commission Amount</TableCell>
                  <TableCell sx={{ color: '#fff', fontWeight: 600, fontSize: '12px', textAlign: 'right' }}>Previous Payable Commission Amount</TableCell>
                  <TableCell sx={{ color: '#fff', fontWeight: 600, fontSize: '12px', textAlign: 'center' }}>{canWriteCommission ? 'Action' : 'Status'}</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {loadingCommission ? (
                  <TableRow>
                    <TableCell colSpan={14} sx={{ textAlign: 'center', py: 4 }}>
                      <CircularProgress />
                      <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
                        Loading commission data...
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : filteredData.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={14} sx={{ textAlign: 'center', py: 4 }}>
                      <Typography variant="body1" color="text.secondary">
                        No data found
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  <>
                    {filteredData.map((row) => (
                      <TableRow
                        key={row.id}
                        hover
                        sx={{
                          '&:nth-of-type(odd)': { backgroundColor: '#f9f9f9' },
                          '&:hover': { backgroundColor: '#f0f7ff !important' },
                        }}
                      >
                        {/* Commented out - Select list checkbox */}
                        {/* <TableCell padding="checkbox">
                          <Checkbox
                            checked={selectedRows.includes(row.id)}
                            onChange={() => handleSelectRow(row.id)}
                          />
                        </TableCell> */}
                        <TableCell>{row.bookingDate}</TableCell>
                        <TableCell>
                          {row.bookingNo ? (
                            <Box
                              component="span"
                              onClick={() =>
                                row.bookingId &&
                                history.push(
                                  `/app/booking/BookingDetails/${row.bookingId}`
                                )
                              }
                              sx={{
                                fontWeight: 700,
                                color: '#536DFE',
                                cursor: row.bookingId ? 'pointer' : 'default',
                                textDecoration: row.bookingId
                                  ? 'underline'
                                  : 'none',
                                '&:hover': row.bookingId
                                  ? { color: '#3F51B5' }
                                  : {},
                              }}
                            >
                              {row.bookingNo}
                            </Box>
                          ) : (
                            '—'
                          )}
                        </TableCell>
                        <TableCell>{row.receivedDate}</TableCell>
                        <TableCell>{row.unitType}</TableCell>
                        <TableCell>{row.project}</TableCell>
                        <TableCell>{row.unitDetail}</TableCell>
                        <TableCell>{row.unitSize}</TableCell>
                        <TableCell>{row.registry}</TableCell>
                        <TableCell>{row.associate}</TableCell>
                        <TableCell>{row.associatePhone}</TableCell>
                        <TableCell sx={{ textAlign: 'right', fontFamily: 'monospace' }}>
                          {row.paymentAmount ? `₹${row.paymentAmount.toLocaleString('en-IN')}` : '₹0'}
                        </TableCell>
                        <TableCell sx={{ textAlign: 'right', fontFamily: 'monospace' }}>
                          {row.payableCommissionPercent.toFixed(2)}
                        </TableCell>
                        <TableCell sx={{ textAlign: 'right', fontFamily: 'monospace' }}>
                          {row.payableCommissionAmount.toFixed(2)}
                        </TableCell>
                        <TableCell sx={{ textAlign: 'right', fontFamily: 'monospace' }}>
                          {row.previousPayableCommissionAmount.toFixed(2)}
                        </TableCell>
                        <TableCell sx={{ textAlign: 'center' }}>
                          {row.isApproved ? (
                            <Chip
                              label="Approved"
                              color="success"
                              size="small"
                              sx={{ fontWeight: 600 }}
                            />
                          ) : canWriteCommission ? (
                            <Button
                              variant="contained"
                              color="primary"
                              size="small"
                              startIcon={<CheckCircleIcon />}
                              onClick={() => handleMakePayableClick(row)}
                              sx={{
                                textTransform: 'none',
                                fontWeight: 600,
                                borderRadius: 1,
                              }}
                            >
                              Make Payable
                            </Button>
                          ) : (
                            <Chip
                              label="Pending"
                              color="warning"
                              size="small"
                              sx={{ fontWeight: 600 }}
                            />
                          )}
                        </TableCell>
                      </TableRow>
                    ))}
                    {/* Total Row */}
                    <TableRow
                      sx={{
                        backgroundColor: '#f5f5f5',
                        '& td': {
                          fontWeight: 700,
                          fontSize: '14px',
                          borderTop: '2px solid #ddd',
                        },
                      }}
                    >
                      <TableCell colSpan={10} sx={{ textAlign: 'right' }}>
                        <strong>Total</strong>
                      </TableCell>
                      <TableCell sx={{ textAlign: 'right', fontFamily: 'monospace' }}>
                        <strong>
                          {totals.paymentAmount ? `₹${totals.paymentAmount.toLocaleString('en-IN')}` : '₹0'}
                        </strong>
                      </TableCell>
                      <TableCell sx={{ textAlign: 'right', fontFamily: 'monospace' }}>
                        {/* Empty cell for percent */}
                      </TableCell>
                      <TableCell sx={{ textAlign: 'right', fontFamily: 'monospace' }}>
                        <strong>
                          {totalPayableCommission.toFixed(2)}
                        </strong>
                      </TableCell>
                      <TableCell sx={{ textAlign: 'right', fontFamily: 'monospace' }}>
                        {/* Empty cell for previous commission or other totals if needed */}
                      </TableCell>
                      <TableCell sx={{ textAlign: 'right', fontFamily: 'monospace' }}>
                        {/* Empty cell for Action / Status */}
                      </TableCell>
                    </TableRow>
                  </>
                )}
              </TableBody>
            </Table>
          </TableContainer>

          {/* Pagination */}
          {totalCount > 0 && (
            <TablePagination
              // rowsPerPageOptions={[5, 10, 25, 50]}
              component="div"
              count={totalCount}
              rowsPerPage={rowsPerPage}
              page={page}
              onPageChange={handleChangePage}
              onRowsPerPageChange={handleChangeRowsPerPage}
            />
          )}
        </CardContent>
      </Card>

      {/* Column Menu */}
      <Menu
        anchorEl={columnMenuAnchor}
        open={Boolean(columnMenuAnchor)}
        onClose={() => setColumnMenuAnchor(null)}
      >
        <MenuItem>Column Options</MenuItem>
      </Menu>

      {/* Confirmation Dialog */}
      <Dialog
        open={confirmDialogOpen}
        onClose={handleCancelMakePayable}
        maxWidth="sm"
        PaperProps={{
          sx: {
            borderRadius: 2,
            boxShadow: '0 8px 32px rgba(0,0,0,0.12)',
          },
        }}
      >
        <DialogTitle
          sx={{
            backgroundColor: '#536DFE',
            color: 'white',
            display: 'flex',
            alignItems: 'center',
            gap: 2,
            py: 3,
            px: 3,
          }}
        >
          <WarningIcon sx={{ fontSize: 28 }} />
          <Typography variant="h6" sx={{ fontWeight: 700, fontSize: '1.1rem' }}>
            Confirm Make Payable
          </Typography>
        </DialogTitle>
        <DialogContent sx={{ p: 3, pt: 3 }}>
          <DialogContentText sx={{ mb: 2, fontSize: '1rem' }}>
            Are you sure you want to make this payable?
          </DialogContentText>
          {selectedCommission && (
            <Box sx={{ mt: 2, p: 2, bgcolor: '#f5f5f5', borderRadius: 1 }}>
              <Typography variant="body2" sx={{ mb: 1 }}>
                <strong>Project:</strong> {selectedCommission.project}
              </Typography>
              <Typography variant="body2" sx={{ mb: 1 }}>
                <strong>Unit:</strong> {selectedCommission.unitDetail}
              </Typography>
              <Typography variant="body2" sx={{ mb: 1 }}>
                <strong>Payable Commission Amount:</strong> ₹{selectedCommission.payableCommissionAmount.toFixed(2)}
              </Typography>
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ p: 3, pt: 2, borderTop: '1px solid #e0e0e0', bgcolor: '#f8f9fa' }}>
          <Button
            onClick={handleCancelMakePayable}
            variant="outlined"
            disabled={updatingCommission}
            sx={{
              textTransform: 'none',
              px: 3,
              py: 1,
              fontWeight: 600,
              borderRadius: 1.5,
            }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleConfirmMakePayable}
            variant="contained"
            color="primary"
            disabled={updatingCommission}
            startIcon={updatingCommission ? <CircularProgress size={16} /> : <CheckCircleIcon />}
            sx={{
              textTransform: 'none',
              px: 3,
              py: 1,
              fontWeight: 600,
              borderRadius: 1.5,
            }}
          >
            {updatingCommission ? 'Processing...' : 'Yes, Make Payable'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default CommissionGenerate;